import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { ReactComponent as ExternalIcon } from "feather-icons/dist/icons/external-link.svg";
import { ReactComponent as CornerUpIcon } from "feather-icons/dist/icons/corner-left-up.svg";
import { formatDate } from "../utils/text";
import { scrollToId } from "../utils/inline";

/**
 * Section « Sources & références » : liste numérotée des sources citées dans l'article ([^n]),
 * avec lien externe, éditeur, type, date et lien de retour vers la première citation.
 */

const Section = tw.section`mt-16`;
const Heading = tw.h2`text-2xl sm:text-3xl font-black text-gray-900 tracking-wide`;
const Intro = tw.p`mt-2 text-sm text-gray-600 font-medium`;
const List = tw.ol`mt-8`;
const Item = styled.li`
  ${tw`flex items-start mt-4 rounded-lg bg-gray-100 p-4 sm:p-5`}
  scroll-margin-top: 6rem;
  &:target {
    ${tw`bg-primary-500 bg-opacity-10`}
  }
`;
const Number = tw.span`flex-shrink-0 w-8 h-8 rounded-full bg-primary-500 text-gray-100 font-bold text-sm flex items-center justify-center mr-4`;
const Body = tw.div`flex-1 min-w-0`;
const TitleLink = styled.a`
  ${tw`font-bold text-gray-900 hocus:text-primary-500 transition duration-300 inline-flex items-start`}
  svg {
    ${tw`w-4 h-4 ml-2 mt-1 flex-shrink-0 text-gray-500`}
  }
`;
const TitleText = tw.span`font-bold text-gray-900`;
const Meta = tw.p`mt-1 text-sm text-gray-600 font-medium break-words`;
const Domain = tw.span`text-gray-500`;
const Back = styled.a`
  ${tw`inline-flex items-center mt-2 text-xs font-bold uppercase tracking-wide text-primary-500 hocus:text-primary-800 transition duration-300`}
  svg {
    ${tw`w-3 h-3 mr-1`}
  }
`;

const TYPE_LABELS = {
  official: "Source officielle",
  press: "Presse",
  academic: "Publication",
  web: "Web",
};

const hostname = url => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch (e) {
    return "";
  }
};

export default function ReferencesList({ references, citedIds }) {
  if (!references || !references.length) return null;
  const cited = citedIds || new Set();
  return (
    <Section id="references" aria-labelledby="references-heading">
      <Heading id="references-heading">Sources &amp; références</Heading>
      <Intro>Les sources citées dans cet article, numérotées dans leur ordre d'apparition dans le texte.</Intro>
      <List>
        {references.map(ref => {
          const n = String(ref.id);
          return (
            <Item key={n} id={`ref-${n}`}>
              <Number aria-hidden="true">{n}</Number>
              <Body>
                {ref.url ? (
                  <TitleLink href={ref.url} target="_blank" rel="noopener noreferrer">
                    <span>{ref.title}</span>
                    <ExternalIcon aria-hidden="true" />
                  </TitleLink>
                ) : (
                  <TitleText>{ref.title}</TitleText>
                )}
                <Meta>
                  {ref.publisher && <span>{ref.publisher}</span>}
                  {ref.type && TYPE_LABELS[ref.type] && <span> · {TYPE_LABELS[ref.type]}</span>}
                  {ref.date && (
                    <span>
                      {" "}
                      · <time dateTime={ref.date}>{formatDate(ref.date)}</time>
                    </span>
                  )}
                  {ref.url && hostname(ref.url) && <Domain> · {hostname(ref.url)}</Domain>}
                </Meta>
                {cited.has(n) && (
                  <Back href={`#cite-${n}`} onClick={e => scrollToId(`cite-${n}`, e)} aria-label={`Retour à la citation ${n} dans le texte`}>
                    <CornerUpIcon aria-hidden="true" />
                    Retour au texte
                  </Back>
                )}
              </Body>
            </Item>
          );
        })}
      </List>
    </Section>
  );
}
